/**
 * usePhotoInspectionGraphQL hook
 *
 * GraphQL hook for fetching detailed photo inspection data for dev tools.
 */

import { gql, useQuery } from '@apollo/client';
import type { PhotoInspection } from '@/types/photoInspection';

const PHOTO_INSPECTION_QUERY = gql`
  query PhotoInspection($id: ID!) {
    photoInspection(id: $id) {
      photo {
        id
        globalId
        userId
        title
        description
        filename
        slug
        mimeType
        width
        height
        fileHash
        fileSize
        metadata {
          width
          height
          format
          fileSize
          camera
          lens
          iso
          aperture
          shutterSpeed
          focalLength
          dateTaken
        }
        createdAt
        updatedAt
      }
      variants {
        id
        photoId
        variantType
        width
        height
        fileSize
        format
        storageUrl
        publicUrl
        versionId
        versionNumber
      }
      collections {
        id
        globalId
        name
        slug
        description
        position
        addedAt
      }
      critiques {
        id
        globalId
        collectionId
        collectionGlobalId
        content
        createdAt
        editId
        criticId
        criticGlobalId
        criticUsername
        criticSlug
        collectionName
      }
      owner {
        id
        globalId
        username
        slug
        email
      }
      stats {
        totalFileSize
        critiqueCount
        collectionCount
        variantCount
      }
    }
  }
`;

export function usePhotoInspectionGraphQL(photoId: string | null | undefined) {
  const { data, loading, error, refetch } = useQuery<{ photoInspection: PhotoInspection }>(
    PHOTO_INSPECTION_QUERY,
    {
      variables: { id: photoId },
      skip: !photoId,
      fetchPolicy: 'cache-and-network',
    }
  );

  return {
    inspection: data?.photoInspection ?? null,
    loading,
    error,
    refetch,
  };
}
